import { createContext, useContext, useState, useEffect, ReactNode } from 'react'
import { getEnvVariable } from '../utils/getEnvVariable'

export interface PortalBranding {
  // eslint-disable-next-line camelcase
  primary_color: string | null
  // eslint-disable-next-line camelcase
  secondary_color: string | null
  // eslint-disable-next-line camelcase
  logo_url: string | null
  // eslint-disable-next-line camelcase
  favicon_url: string | null
  // eslint-disable-next-line camelcase
  portal_title: string | null
  // eslint-disable-next-line camelcase
  login_background_color: string | null
  // eslint-disable-next-line camelcase
  header_color: string | null
  // eslint-disable-next-line camelcase
  header_text_color: string | null
  // eslint-disable-next-line camelcase
  background_color: string | null
  // eslint-disable-next-line camelcase
  font_family: string | null
  // eslint-disable-next-line camelcase
  border_radius: number | null
  // eslint-disable-next-line camelcase
  login_subtitle: string | null
  // eslint-disable-next-line camelcase
  admin_title: string | null
}

/* eslint-disable camelcase */
const DEFAULT_BRANDING: PortalBranding = {
  primary_color: null,
  secondary_color: null,
  logo_url: null,
  favicon_url: null,
  portal_title: null,
  login_background_color: null,
  header_color: null,
  header_text_color: null,
  background_color: null,
  font_family: null,
  border_radius: null,
  login_subtitle: null,
  admin_title: null,
}
/* eslint-enable camelcase */

const CACHE_KEY = 'admin_portal_branding'
const API_URL = getEnvVariable('REACT_APP_SERVER_URL')

const getCachedBranding = (): PortalBranding => {
  try {
    const cached = sessionStorage.getItem(CACHE_KEY)
    if (cached) return JSON.parse(cached) as PortalBranding
  } catch { /* ignore */ }
  return DEFAULT_BRANDING
}

const BrandingContext = createContext<PortalBranding>(DEFAULT_BRANDING)

export const useBranding = (): PortalBranding => useContext(BrandingContext)

export const BrandingProvider = ({ children }: { children: ReactNode }): JSX.Element => {
  const [branding, setBranding] = useState<PortalBranding>(getCachedBranding)

  useEffect(() => {
    const controller = new AbortController()
    const timeoutId = setTimeout(() => controller.abort(), 5000)

    fetch(`${API_URL}/portal/branding`, { signal: controller.signal })
      .then(res => (res.ok ? res.json() : DEFAULT_BRANDING))
      .then((data: PortalBranding) => {
        setBranding(data)
        sessionStorage.setItem(CACHE_KEY, JSON.stringify(data))

        if (data.admin_title) {
          document.title = data.admin_title
        }
        if (data.favicon_url) {
          const iconLinks = document.querySelectorAll<HTMLLinkElement>(
            'link[rel="icon"], link[rel="alternate icon"], link[rel="apple-touch-icon"]'
          )
          iconLinks.forEach(l => { l.href = data.favicon_url! })
          if (iconLinks.length === 0) {
            const link = document.createElement('link')
            link.rel = 'icon'
            link.href = data.favicon_url
            document.head.appendChild(link)
          }
        }
        if (data.font_family) {
          const fontName = encodeURIComponent(data.font_family)
          let fontLink = document.querySelector<HTMLLinkElement>('link[data-google-font]')
          if (!fontLink) {
            fontLink = document.createElement('link')
            fontLink.rel = 'stylesheet'
            fontLink.setAttribute('data-google-font', 'true')
            document.head.appendChild(fontLink)
          }
          fontLink.href = `https://fonts.googleapis.com/css2?family=${fontName}:wght@400;500;600;700&display=swap`
        }
      })
      .catch((e: unknown) => {
        if (e instanceof Error && e.name !== 'AbortError') {
          console.error('Failed to load branding:', e)
        }
      })
      .finally(() => {
        clearTimeout(timeoutId)
      })

    return () => {
      controller.abort()
      clearTimeout(timeoutId)
    }
  }, [])

  return (
    <BrandingContext.Provider value={branding}>
      {children}
    </BrandingContext.Provider>
  )
}
